import { useEffect, useState, useRef, useCallback } from 'react'
import * as api from '../api/users'

// Вспомогательная функция для получения токена
const getAuthToken = () => localStorage.getItem('authToken');

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [objects, setObjects] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false); // Предотвращаем множественные одновременные запросы

  const fetchObjects = useCallback(async () => {
    try {
      const response = await api.getObjects();
      setObjects(response.data || response || []);
    } catch (err) {
      // Non-blocking for the users table; school picker just stays empty.
      console.warn('Failed to load objects for admin picker', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    // Предотвращаем множественные одновременные запросы
    if (fetchingRef.current) {
      return;
    }

    const token = getAuthToken();
    
    // Если токена нет, не делаем запрос
    if (!token) {
      setError('Ошибка аутентификации. Пожалуйста, войдите снова.');
      setLoading(false);
      return;
    }

    // Проверяем токен перед запросом (для отладки)
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      const isExpired = tokenData.exp < now;
      

      if (isExpired) {
        setError('Токен истек. Пожалуйста, перезайдите в систему.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
        setLoading(false);
        return;
      }

      // Нормализуем роль для сравнения
      const tokenRole = tokenData.role ? (typeof tokenData.role === 'string' ? tokenData.role.trim().toLowerCase() : tokenData.role) : null;
      if (tokenRole !== 'admin') {
      }
    } catch (e) {
      setError('Токен поврежден. Пожалуйста, перезайдите в систему.');
      setLoading(false);
      return;
    }

    fetchingRef.current = true;

    try {
      setLoading(true);
      setError('');
      const response = await api.getUsers();
      // Бэкенд возвращает { data: [...], meta: {...} }
      setUsers(response.data || response);
    } catch (err) {
      
      // Если 404, возможно пользователь не админ или токен невалидный
      if (err.status === 404 || err.message.includes('404') || err.message.includes('Not found')) {
        // Проверяем токен еще раз для более точного сообщения
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          const tokenRole = tokenData.role ? (typeof tokenData.role === 'string' ? tokenData.role.trim().toLowerCase() : tokenData.role) : null;
          if (tokenRole !== 'admin') {
            setError(`Доступ запрещен. Ваша роль: "${tokenData.role}" (нормализованная: "${tokenRole}"), требуется: "admin".`);
          } else {
            setError('Доступ запрещен. Требуется роль администратора или токен невалидный.');
          }
        } catch {
          setError('Доступ запрещен. Требуется роль администратора или токен невалидный.');
        }
      } else if (err.status === 401 || err.status === 403) {
        setError('Ошибка аутентификации. Пожалуйста, войдите снова.');
        // Очищаем токен при ошибке аутентификации
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
      } else {
        setError(err.message || 'Ошибка при загрузке пользователей');
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);


  const addUser = async (user) => {
    try {
      setError('');
      await api.createUser(user);
      setSuccess('Пользователь успешно создан');
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Ошибка при создании пользователя');
    }
  };

  const updateUser = async (id, role, objectIds) => {
    try {
      setError('');
      await api.updateUserRole(id, role, objectIds);
      setSuccess('Роль обновлена');
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Ошибка при обновлении роли');
    }
  };

  const updateUserSchools = async (id, objectIds) => {
    try {
      setError('');
      await api.updateUserObjects(id, objectIds);
      setSuccess('Школы обновлены');
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Ошибка при обновлении школ');
    }
  };

  const deleteUser = async (id) => {
    try {
      setError('');
      await api.deleteUser(id);
      setSuccess('Пользователь удален');
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Ошибка при удалении пользователя');
    }
  };


  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      fetchUsers();
      fetchObjects();
    }
  }, [fetchUsers, fetchObjects]);

  // Слушаем изменения токена через событие storage (для других вкладок) и кастомное событие (для текущей вкладки)
  useEffect(() => {
    let timeoutId = null;

    const handleStorageChange = (e) => {
      if (e.key === 'authToken') {
        // Токен изменился - перезагружаем данные
        if (e.newValue) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            setError(''); // Очищаем предыдущую ошибку
            fetchUsers();
          }, 100);
        } else {
          // Токен удален - очищаем данные
          setUsers([]);
          setError('');
        }
      }
    };

    // Слушаем кастомное событие при обновлении токена (для текущей вкладки)
    const handleAuthTokenUpdated = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const token = getAuthToken();
        if (token) {
          setError(''); // Очищаем предыдущую ошибку
          fetchUsers();
        }
      }, 100);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authTokenUpdated', handleAuthTokenUpdated);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authTokenUpdated', handleAuthTokenUpdated);
    };
  }, [fetchUsers]);

  return { 
    users,
    objects,
    error, 
    success, 
    loading,
    addUser, 
    updateUser,
    updateUserSchools,
    deleteUser, 
    setError, 
    setSuccess,
    refetchUsers: fetchUsers
  };
};
